/**
 * Home dashboard. Greeting, hero clay-gradient CTA, enrollment nudge,
 * recent conversations.
 */
import { useEffect, useState } from "react";
import * as api from "../api";
import { authStore } from "../store";
import { Btn, LangList, langByCode, useLanguages } from "../ui";
import { Icon } from "../icons";
import type { Go } from "../App";

export function Dashboard({ go, enrollments }: { go: Go; enrollments: api.EnrollmentSummary[] }) {
  const user = authStore((s) => s.user);
  const [sessions, setSessions] = useState<api.SessionListItem[]>([]);
  const [pickFor, setPickFor] = useState<"you" | "guest" | null>(null);
  const [query, setQuery] = useState("");
  useLanguages(); // warm cache
  useEffect(() => {
    api.listSessions().then(setSessions).catch(() => setSessions([]));
  }, []);

  const hasActive = enrollments.some((e) => e.active);
  const recent = sessions.slice(0, 3);
  const youCode = user?.owner_lang ?? "en-US";
  const guestCode = user?.other_lang_override ?? user?.fallback_other_lang ?? "hi-IN";

  async function pickLang(code: string) {
    const field = pickFor === "you" ? "owner_lang" : "other_lang_override";
    setPickFor(null); setQuery("");
    try {
      const me = await api.patchMe({ [field]: code });
      authStore.getState().setUser(me);
    } catch { /* ignore */ }
  }

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "40px 28px 60px" }}>
      <div className="mono" style={{
        fontSize: 11, letterSpacing: 1.6, color: "var(--accent)",
        textTransform: "uppercase", marginBottom: 10,
      }}>Today</div>
      <h1 className="display" style={{
        fontSize: 36, fontWeight: 700, letterSpacing: -0.8, color: "var(--ink)", margin: 0,
      }}>
        Hi {user?.display_name?.split(" ")[0] ?? "there"} — ready to talk?
      </h1>

      {/* Hero card */}
      <button onClick={() => go("live")} style={{
        marginTop: 28, width: "100%", textAlign: "left", cursor: "pointer", border: "none",
        background: "linear-gradient(135deg, var(--you) 0%, var(--accent) 60%, var(--accent-press) 100%)",
        color: "var(--on-accent)", borderRadius: "var(--r-xl)", padding: "36px 32px",
        boxShadow: "var(--shadow-md)", display: "flex", alignItems: "center", gap: 24,
      }}>
        <div style={{ flex: 1 }}>
          <div className="mono" style={{ fontSize: 11, letterSpacing: 1.6, opacity: 0.85, textTransform: "uppercase", marginBottom: 8 }}>
            Live conversation
          </div>
          <div className="display" style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, letterSpacing: -0.6 }}>
            Start a live translation
          </div>
          <div style={{ marginTop: 10, opacity: 0.85, fontSize: 14.5, display: "flex", alignItems: "center", gap: 6 }}>
            <button onClick={(e) => { e.stopPropagation(); setPickFor("you"); setQuery(""); }} style={{
              background: "rgba(255,255,255,0.18)", border: "none", borderRadius: "var(--r-pill)",
              padding: "4px 10px", cursor: "pointer", color: "inherit", fontFamily: "var(--font-ui)",
              fontSize: 14, fontWeight: 600,
            }}>{langByCode(youCode).name}</button>
            <span>⇄</span>
            <button onClick={(e) => { e.stopPropagation(); setPickFor("guest"); setQuery(""); }} style={{
              background: "rgba(255,255,255,0.18)", border: "none", borderRadius: "var(--r-pill)",
              padding: "4px 10px", cursor: "pointer", color: "inherit", fontFamily: "var(--font-ui)",
              fontSize: 14, fontWeight: 600,
            }}>{langByCode(guestCode).name}</button>
          </div>
        </div>
        <div style={{
          width: 72, height: 72, borderRadius: "50%", background: "rgba(255,255,255,0.18)",
          display: "grid", placeItems: "center", flexShrink: 0,
        }}>
          <Icon name="mic" size={32} stroke={2.2} color="var(--on-accent)" />
        </div>
      </button>

      {/* Language picker overlay */}
      {pickFor && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)",
          display: "grid", placeItems: "center",
        }} onClick={() => { setPickFor(null); setQuery(""); }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "var(--surface)", borderRadius: "var(--r-xl)",
            width: "min(420px, 90vw)", maxHeight: "70vh", overflowY: "auto",
            boxShadow: "var(--shadow-lg)", padding: "18px 0",
          }}>
            <div style={{ padding: "0 22px 12px", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>
              {pickFor === "you" ? "Your language" : "Guest language"}
            </div>
            <LangList
              activeCode={pickFor === "you" ? youCode : guestCode}
              who={pickFor === "you" ? "you" : "guest"}
              onPick={pickLang}
              query={query} setQuery={setQuery}
              showRecents recents={["hi-IN", "en-US", "es-ES"]}
            />
          </div>
        </div>
      )}

      {!hasActive && (
        <div style={{
          marginTop: 18, background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--r-lg)", padding: "18px 20px",
          display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14, display: "grid", placeItems: "center",
            background: "var(--accent-soft)", color: "var(--accent-ink)", flexShrink: 0,
          }}>
            <Icon name="sparkle" size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>Enroll your voice</div>
            <div style={{ fontSize: 13.5, color: "var(--ink-3)", marginTop: 2 }}>
              Record a quick voiceprint so we always know it's you.
            </div>
          </div>
          <Btn size="sm" variant="tonal" onClick={() => go("enroll")}>Record</Btn>
        </div>
      )}

      <div style={{ marginTop: 40, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <h2 className="display" style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", margin: 0, letterSpacing: -0.3 }}>
          Recent conversations
        </h2>
        <button onClick={() => go("history")} style={{
          background: "transparent", border: "none", cursor: "pointer", color: "var(--accent-ink)",
          fontFamily: "var(--font-ui)", fontSize: 14, fontWeight: 600,
          display: "flex", alignItems: "center", gap: 4,
        }}>View all <Icon name="chevronRight" size={16} /></button>
      </div>
      {recent.length === 0 ? (
        <div style={{ marginTop: 16, color: "var(--ink-3)", fontSize: 14 }}>No conversations yet.</div>
      ) : (
        <div style={{ marginTop: 16, display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
          {recent.map((s) => <RecentCard key={s.id} s={s} onOpen={() => go("session", { sessionId: s.id })} />)}
        </div>
      )}
    </div>
  );
}

function RecentCard({ s, onOpen }: { s: api.SessionListItem; onOpen: () => void }) {
  const d = new Date(s.started_at);
  const title = d.toLocaleDateString(undefined, { month: "short", day: "numeric", weekday: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return (
    <button onClick={onOpen} style={{
      textAlign: "left", cursor: "pointer", border: "1px solid var(--border)",
      background: "var(--surface)", borderRadius: "var(--r-lg)", padding: "16px 18px",
      display: "flex", flexDirection: "column", gap: 8,
      transition: "transform 0.12s var(--ease), box-shadow 0.15s",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "var(--shadow-md)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{time} · {s.utterance_count} utterances</div>
      <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 6, color: "var(--accent-ink)", fontSize: 13, fontWeight: 600 }}>
        Open <Icon name="arrowRight" size={14} />
      </div>
    </button>
  );
}
